import { ScopeDIProvider } from '@repo/di-ez';
import { LicenseInfo } from '@repo/go-table';
import { ConfigProvider, en, ru } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import i18 from 'src/i18n';
import { Router } from 'src/router/router';

import { agent } from './api/agent';
import styles from './app.module.scss';
import { WellsService } from './services';
import { UserSettingsService, UserSettingsServiceApi } from './services/user-settings-service';
import { Loader } from './shared/components/loader';
import { LANGUAGES } from './shared/constants/languages';
import { RootStore, RootStoreProvider } from './store';

import './styles/index.scss';
type TUserSettingsSections = 'wellsList';

export const MUI_KEY =
  '41e30292e843764d4d525ef823c50677Tz04MTE4NixFPTE3MzUyNjk2MzgwMDAsUz1wcmVtaXVtLExNPXBlcnBldHVhbCxLVj0y';

LicenseInfo.setLicenseKey(MUI_KEY);

export const App = observer(function App() {
  const navigate = useNavigate();
  const [store] = useState(() => new RootStore({ navigate, i18 }));
  const [wellsService] = useState(() => new WellsService(store.directories, store.planVersionsService));

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', store.theme.theme);
  }, [store.theme.theme]);

  useEffect(() => {
    const disposeStore = store.init();

    return () => {
      disposeStore();
    };
  }, [store]);

  const container = document.querySelector('body');

  useEffect(() => {
    container?.classList.add('container');
  }, [container]);

  const init = useCallback(() => {
    return {
      wellsService,
      userSettings: new UserSettingsService<TUserSettingsSections>(
        new UserSettingsServiceApi(agent, store.auth.userInfo?.sub ?? ''),
      ),
    };
  }, [store.auth.userInfo?.sub, wellsService]);

  if (!store.isInitialized || store.isLoading || !store.auth.isAuthenticated) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <ConfigProvider
      config={{
        theme: store.theme.theme,
        size: 's',
        style: 'bordered',
        localization: store.language.language === LANGUAGES.ru ? ru : en,
      }}
      externalElement={container ?? undefined}
    >
      <ScopeDIProvider init={init}>
        <RootStoreProvider store={store}>
          <Router />
        </RootStoreProvider>
      </ScopeDIProvider>
    </ConfigProvider>
  );
});

declare module '@repo/di-ez' {
  interface ServicesCollectionMap {
    wellsService: WellsService;
    userSettings: UserSettingsService<TUserSettingsSections>;
  }
}
